/**
 * 章节类型定义
 *
 * 定义章节相关的TypeScript类型，供前端组件、服务和状态管理使用
 */

/**
 * Diff差异类型枚举
 * - equal: 未修改
 * - insert: 新增
 * - delete: 删除
 * - replace: 替换
 */
export enum DiffType {
  EQUAL = 'equal',
  INSERT = 'insert',
  DELETE = 'delete',
  REPLACE = 'replace'
}

/**
 * 锁定范围类型
 *
 * 锁定文字的范围，格式为 [start_index, end_index]
 * - start_index: 起始索引(包含)
 * - end_index: 结束索引(不包含)
 */
export type LockRange = [number, number];

/**
 * 章节创建请求接口
 *
 * 对应后端 ChapterCreate Schema
 * 用于创建新章节的请求参数
 */
export interface ChapterCreate {
  /** 所属工作区ID */
  workspace_id: number;
  /** 章节序号，范围1-100000 */
  num: number;
  /** 章节名称，长度1-200字符 */
  name: string;
  /** 章节正文内容(Markdown格式)，最大5MB，可选 */
  content?: string;
}

/**
 * 章节更新请求接口
 *
 * 对应后端 ChapterUpdate Schema
 * 用于更新现有章节的请求参数，所有字段可选
 */
export interface ChapterUpdate {
  /** 章节名称(可选) */
  name?: string;
  /** 章节正文内容(可选) */
  content?: string;
  /** 锁定范围列表(可选) */
  locked_ranges?: LockRange[];
}

/**
 * 章节完整信息接口
 *
 * 对应后端 ChapterResponse Schema
 * 表示章节对象的完整信息，包括系统生成的字段
 */
export interface Chapter {
  /** 章节唯一标识 */
  id: number;
  /** 所属工作区ID */
  workspace_id: number;
  /** 章节序号 */
  num: number;
  /** 章节名称 */
  name: string;
  /** 章节正文内容(Markdown格式) */
  content: string | null;
  /** 章节字数 */
  word_count: number;
  /** 章节摘要 */
  chapter_abstract: string | null;
  /** 摘要更新时间(ISO 8601格式) */
  abstract_updated_at: string | null;
  /** 所属分支ID，null表示主线 */
  branch_id: number | null;
  /** 是否为公共章节（分支点之前的章节） */
  is_common: boolean;
  /** 锁定文字范围(JSON数组字符串) - 保留兼容性，但不再使用 */
  locked_ranges: string | null;
  /** 用户手动锁定（防止AI修改） */
  is_locked: boolean;
  /** 创建时间(ISO 8601格式) */
  created_at: string;
  /** 更新时间(ISO 8601格式) */
  updated_at: string;
}

/**
 * 章节编辑历史版本接口
 *
 * 对应后端 API 响应
 */
export interface ChapterVersion {
  /** 版本唯一标识 */
  id: number;
  /** 所属章节ID */
  chapter_id: number;
  /** 版本号 */
  version: number;
  /** 版本内容 */
  content: string;
  /** 字数 */
  word_count: number;
  /** 编辑者类型 (user/agent) */
  editor_type: 'user' | 'agent';
  /** 编辑者名称 */
  editor_name: string | null;
  /** 创建时间(ISO 8601格式) */
  created_at: string;
}

/**
 * 编辑历史分页响应接口
 *
 * 获取章节编辑历史的响应结构，包含版本列表和分页信息
 */
export interface ChapterHistory {
  /** 版本列表 */
  versions: ChapterVersion[];
  /** 总条数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页条数 */
  page_size: number;
  /** 总页数 */
  total_pages: number;
}

/**
 * Diff差异项接口
 *
 * 版本对比的差异项
 */
export interface DiffItem {
  /** 差异类型 */
  type: DiffType;
  /** 内容 */
  content: string;
}

/**
 * Diff结果接口
 *
 * 版本对比的 API 响应
 */
export interface DiffResult {
  /** 版本号 */
  version: number;
  /** 新增行数 */
  additions: number;
  /** 删除行数 */
  deletions: number;
  /** Diff内容 */
  diff: string;
}

/**
 * 章节列表筛选条件接口
 *
 * 章节列表查询的筛选条件
 */
export interface ChapterFilters {
  /** 章节序号或序号列表(可选) */
  num?: number | number[];
  /** 按名称模糊匹配(可选) */
  name?: string;
}

// ==================== 类型守卫函数 ====================

/**
 * 类型守卫函数，验证章节序号是否有效
 *
 * @param value - 待验证的值
 * @returns 如果是有效的章节序号返回true，否则返回false
 */
export function isValidChapterNum(value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    return false;
  }
  return value >= 1 && value <= 100000;
}

/**
 * 类型守卫函数，验证章节名称是否有效
 *
 * @param value - 待验证的值
 * @returns 如果是有效的章节名称返回true，否则返回false
 */
export function isValidChapterName(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return trimmed.length >= 1 && trimmed.length <= 200;
}

/**
 * 类型守卫函数，验证锁定范围是否有效
 *
 * @param value - 待验证的值
 * @returns 如果是有效的锁定范围返回true，否则返回false
 */
export function isValidLockRange(value: unknown): value is LockRange {
  if (!Array.isArray(value) || value.length !== 2) {
    return false;
  }
  const [start, end] = value;
  if (typeof start !== 'number' || typeof end !== 'number') {
    return false;
  }
  if (!Number.isInteger(start) || !Number.isInteger(end)) {
    return false;
  }
  return start >= 0 && end > start;
}

/**
 * 验证锁定范围列表是否重叠
 *
 * @param ranges - 锁定范围列表
 * @returns 如果范围有效且不重叠返回true，否则返回false
 */
export function validateLockRanges(ranges: LockRange[]): boolean {
  // 验证每个范围是否有效
  if (!ranges.every(isValidLockRange)) {
    return false;
  }

  // 检查范围是否重叠
  const sortedRanges = [...ranges].sort((a, b) => a[0] - b[0]);
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    if (sortedRanges[i][1] > sortedRanges[i + 1][0]) {
      return false;
    }
  }

  return true;
}